import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BusinessProfile } from '@kheops/graphql';
import { useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { SUPPORTED_IMAGE_TYPES, useUploadFile } from '../../hooks/useUploadFile';
import SizedImage from '../../common/components/SizedImage';
import FileUtils from '../../common/utils/file.utils';
import { CompanyProfileForm } from './form.type';

interface CompanyPhotoFormProps {
  companyId: string;
}
export default function CompanyPhotoForm({ companyId }: CompanyPhotoFormProps): React.JSX.Element {
  const { t } = useTranslation('common');
  const { setValue, watch } = useFormContext<CompanyProfileForm>();
  const displayedPhoto = watch('photo');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    handleFileChange,
    isFileUploadLoading,
  } = useUploadFile();

  const handleButtonClick = (): void => {
    fileInputRef.current!.click();
  };

  const fileChanged = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileParameters = await handleFileChange(event.target.files![0], 'COMPANY_PHOTOS', companyId);
    setValue('photo', fileParameters, { shouldDirty: true });

    // empty the value attribute of the input file so we can re-upload the same file in case of error
    event.target.value = '';
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        flexDirection: {
          xs: 'column',
          sm: 'row',
        },
      }}
    >
      <SizedImage
        src={
          FileUtils.BuildCompanyImageUrl({
            photo: displayedPhoto,
            type: BusinessProfile.Supplier,
            size: {
              height: 124,
              width: 124,
              fit: 'cover',
            },
          })
        }
        sx={{
          borderRadius: 6,
        }}
        height={124}
        width={124}
        alt="company_photo"
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography variant="bodySmall">{t('common:profile_image')}</Typography>
        <Button
          disabled={isFileUploadLoading}
          variant="outlined"
          startIcon={isFileUploadLoading ? <CircularProgress /> : <AddAPhotoOutlinedIcon />}
          onClick={handleButtonClick}
        >
          {t('common:change_photo')}
        </Button>
      </Box>
      <Box
        ref={fileInputRef}
        component="input"
        type="file"
        accept={SUPPORTED_IMAGE_TYPES.join(', ')}
        sx={{ display: 'none' }}
        onChange={fileChanged}
      />
    </Box>
  );
}
